import React, { useState, useEffect } from "react";
import { getAllUsers, updateUserRole, subscribeToRAStaff, createRAStaff, updateRAStaff } from "./firebase";

const FACILITIES = ["Dorcus 1 - 801", "Dorcus 2 - 24th St"];
const ROLE_OPTIONS = [
  { value:"pending",        label:"Pending",           color:"#7A4F00", bg:"#FFF3CD", border:"#F5C200" },
  { value:"admin",          label:"Admin",              color:"#8B1A1A", bg:"#FDECEA", border:"#F5A8A8" },
  { value:"pm",             label:"Program Manager",    color:"#4A3AAA", bg:"#F0EEFB", border:"#A89FE0" },
  { value:"rc",             label:"Ref. Coordinator",   color:"#0D5C54", bg:"#E6F6F4", border:"#7DCEC8" },
  { value:"house_computer", label:"House Computer",     color:"#1B5EBE", bg:"#E8F0FB", border:"#B5D4F4" },
];
const FACILITY_FOR_ROLE = { ra_a:"Dorcus 1 - 801", ra_b:"Dorcus 2 - 24th St" };

const DS = {
  blue:"#1B5EBE", darkBlue:"#154A99", blueTint:"#E8F0FB", blueBorder:"#B5D4F4",
  pageBg:"#C8D8EC", border:"#D8DCE3", textPrimary:"#1A2B45", textSec:"#4A5568",
  white:"#FFFFFF", inputBg:"#F0F4FA",
  success:{ bg:"#E6F4EC", text:"#1A6B35", border:"#A3D4B4" },
  error:  { bg:"#FDECEA", text:"#8B1A1A", border:"#F5A8A8" },
  warning:{ bg:"#FFF3CD", text:"#7A4F00", border:"#F5C200" },
};

const S = {
  card:     { background:"white", border:`0.5px solid ${DS.border}`, borderRadius:8, padding:14 },
  input:    { background:DS.inputBg, border:`1px solid ${DS.border}`, borderRadius:8, padding:"8px 12px", fontSize:13, color:DS.textPrimary, width:"100%", outline:"none", boxSizing:"border-box", fontFamily:"inherit" },
  btn:      { background:DS.blue, color:"white", border:"none", borderRadius:8, padding:"8px 16px", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit" },
  btnGhost: { background:"white", color:DS.textSec, border:`1px solid ${DS.border}`, borderRadius:8, padding:"8px 16px", fontSize:13, cursor:"pointer", fontFamily:"inherit" },
};

function initials(name) {
  return (name||"?").split(" ").filter(Boolean).map(x=>x[0]).join("").toUpperCase().slice(0,2);
}

// ── Google Accounts Tab ───────────────────────────────────
function GoogleAccountsTab() {
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState({});
  const [search, setSearch]   = useState("");

  const load = async () => {
    setLoading(true);
    const all = await getAllUsers();
    // Show all accounts including house_computer so admin can assign the role
    all.sort((a,b) => (a.email||"").localeCompare(b.email||""));
    setUsers(all);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleRoleChange = async (uid, newRole) => {
    setSaving(s => ({ ...s, [uid]: true }));
    const facility = FACILITY_FOR_ROLE[newRole] || null;
    await updateUserRole(uid, newRole, facility);
    setSaving(s => ({ ...s, [uid]: false }));
    await load();
  };

  const filtered = users.filter(u =>
    !search ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.displayName?.toLowerCase().includes(search.toLowerCase())
  );

  const roleCounts = ROLE_OPTIONS.reduce((acc,r) => {
    acc[r.value] = users.filter(u=>u.role===r.value).length;
    return acc;
  }, {});

  return (
    <div>
      <div style={{ display:"flex", gap:10, marginBottom:12 }}>
        <input style={{ ...S.input, flex:1 }} placeholder="Search by name or email..."
          value={search} onChange={e => setSearch(e.target.value)} />
        <button onClick={load} style={{ ...S.btnGhost, padding:"8px 14px", fontSize:12 }}>Refresh</button>
      </div>

      <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:14 }}>
        {ROLE_OPTIONS.map(r => roleCounts[r.value]>0&&(
          <div key={r.value} style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, padding:"2px 8px", borderRadius:8, background:r.bg, border:`1px solid ${r.border}`, color:r.color }}>
            <div style={{ width:6, height:6, borderRadius:"50%", background:r.color, flexShrink:0 }}/>
            {r.label}: {roleCounts[r.value]}
          </div>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign:"center", padding:"40px 0", color:DS.textSec, fontSize:13 }}>Loading...</div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
          {filtered.map(user => {
            const roleInfo = ROLE_OPTIONS.find(r => r.value === user.role) || ROLE_OPTIONS[0];
            return (
              <div key={user.uid} style={{ ...S.card, display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
                <div style={{ width:38, height:38, borderRadius:9, background:DS.blueTint, border:`1px solid ${DS.blueBorder}`, display:"flex", alignItems:"center", justifyContent:"center", color:DS.blue, fontWeight:700, fontSize:13, flexShrink:0 }}>
                  {initials(user.displayName||user.email)}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:DS.textPrimary, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{user.displayName||user.email}</div>
                  <div style={{ fontSize:11, color:DS.textSec }}>{user.email}</div>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <div style={{ width:7, height:7, borderRadius:"50%", background:roleInfo.color, flexShrink:0 }}/>
                  <select
                    value={user.role||"pending"}
                    onChange={e => handleRoleChange(user.uid, e.target.value)}
                    disabled={saving[user.uid]}
                    style={{ background:roleInfo.bg, border:`1px solid ${roleInfo.border}`, borderRadius:8, padding:"5px 10px", fontSize:12, color:roleInfo.color, cursor:"pointer", outline:"none", fontFamily:"inherit" }}>
                    {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                  {saving[user.uid] && <span style={{ fontSize:11, color:DS.textSec }}>Saving...</span>}
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div style={{ textAlign:"center", padding:"40px 0", color:DS.textSec, fontSize:13 }}>No accounts found.</div>
          )}
        </div>
      )}

      <div style={{ marginTop:14, padding:"10px 14px", background:DS.blueTint, border:`1px solid ${DS.blueBorder}`, borderRadius:8 }}>
        <p style={{ fontSize:12, color:DS.textSec, margin:0, lineHeight:1.6 }}>
          <strong style={{ color:DS.blue }}>Note:</strong> When a new staff member signs in with Google for the first time, they appear here as "Pending". Assign <strong>House Computer</strong> to front desk accounts — they will be routed to the RA PIN screen on login. RA PIN accounts are managed in the RA Accounts tab.
        </p>
      </div>
    </div>
  );
}

// ── RA Accounts Tab ───────────────────────────────────────
function RAAccountsTab() {
  const [raStaff, setRaStaff]     = useState([]);
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState({ name:"", facility:"Dorcus 1 - 801", pin:"", confirmPin:"" });
  const [editingId, setEditingId] = useState(null);
  const [editPin, setEditPin]     = useState("");
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState("");
  const [facFilter, setFacFilter] = useState("all");

  useEffect(() => {
    // Load all RA staff across both facilities
    const unsub = subscribeToRAStaff(null, setRaStaff);
    return unsub;
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleCreate = async () => {
    setError("");
    if (!form.name.trim())                              return setError("Name is required.");
    if (form.pin.length !== 4 || !/^\d{4}$/.test(form.pin)) return setError("PIN must be exactly 4 digits.");
    if (form.pin !== form.confirmPin)                   return setError("PINs do not match.");
    setSaving(true);
    await createRAStaff({ name:form.name.trim(), facility:form.facility, pin:form.pin });
    setForm({ name:"", facility:"Dorcus 1 - 801", pin:"", confirmPin:"" });
    setShowForm(false);
    setSaving(false);
  };

  const handleResetPin = async (ra) => {
    if (!editPin || editPin.length !== 4 || !/^\d{4}$/.test(editPin)) return;
    await updateRAStaff(ra.id, { pin:editPin });
    setEditingId(null);
    setEditPin("");
  };

  const handleDeactivate = async (ra) => {
    if (!window.confirm(`Remove ${ra.name} from the system?`)) return;
    await updateRAStaff(ra.id, { active:false });
  };

  const filtered = raStaff.filter(r => facFilter==="all" || r.facility===facFilter);

  const facilityColor = (f) => f?.includes("801") ? DS.blue : "#0D5C54";
  const facilityBg    = (f) => f?.includes("801") ? DS.blueTint : "#E6F6F4";

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:8 }}>
        <div style={{ display:"flex", gap:6 }}>
          {["all", ...FACILITIES].map(f => (
            <button key={f} onClick={() => setFacFilter(f)}
              style={{ padding:"5px 12px", fontSize:12, borderRadius:7, cursor:"pointer",
                background:facFilter===f ? DS.blue : "white",
                border:`1px solid ${facFilter===f ? DS.blue : DS.border}`,
                color:facFilter===f ? "white" : DS.textSec, fontFamily:"inherit" }}>
              {f==="all" ? "All Houses" : f.replace("Dorcus ","")}
            </button>
          ))}
        </div>
        <button onClick={() => setShowForm(s => !s)} style={{ ...S.btn, padding:"7px 14px", fontSize:12 }}>
          {showForm ? "Cancel" : "+ Add RA"}
        </button>
      </div>

      {showForm && (
        <div style={{ ...S.card, border:`1px solid ${DS.blueBorder}`, background:DS.blueTint, marginBottom:14 }}>
          <div style={{ fontSize:13, fontWeight:600, color:DS.blue, marginBottom:12, fontFamily:"'DM Sans',sans-serif" }}>New RA Account</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
            <div>
              <div style={{ fontSize:10, color:DS.textSec, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:3 }}>Full Name *</div>
              <input style={{ ...S.input }} placeholder="RA name..." value={form.name} onChange={e => set("name", e.target.value)} />
            </div>
            <div>
              <div style={{ fontSize:10, color:DS.textSec, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:3 }}>Primary House *</div>
              <select style={{ ...S.input }} value={form.facility} onChange={e => set("facility", e.target.value)}>
                {FACILITIES.map(f => <option key={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize:10, color:DS.textSec, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:3 }}>4-Digit PIN *</div>
              <input style={{ ...S.input }} type="password" inputMode="numeric" maxLength={4} placeholder="····"
                value={form.pin} onChange={e => set("pin", e.target.value.replace(/\D/g,"").slice(0,4))} />
            </div>
            <div>
              <div style={{ fontSize:10, color:DS.textSec, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:3 }}>Confirm PIN *</div>
              <input style={{ ...S.input }} type="password" inputMode="numeric" maxLength={4} placeholder="····"
                value={form.confirmPin} onChange={e => set("confirmPin", e.target.value.replace(/\D/g,"").slice(0,4))} />
            </div>
          </div>
          {error && <p style={{ fontSize:12, color:DS.error.text, margin:"0 0 10px" }}>{error}</p>}
          <div style={{ display:"flex", justifyContent:"flex-end", gap:8 }}>
            <button onClick={() => { setShowForm(false); setError(""); }} style={{ ...S.btnGhost, padding:"7px 14px", fontSize:12 }}>Cancel</button>
            <button onClick={handleCreate} disabled={saving} style={{ ...S.btn, opacity:saving?0.6:1, padding:"7px 14px", fontSize:12 }}>
              {saving ? "Saving..." : "Create RA Account"}
            </button>
          </div>
        </div>
      )}

      <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
        {filtered.map(ra => (
          <div key={ra.id} style={{ ...S.card }}>
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              <div style={{ width:38, height:38, borderRadius:9, background:facilityBg(ra.facility), border:`1px solid ${facilityColor(ra.facility)}33`, display:"flex", alignItems:"center", justifyContent:"center", color:facilityColor(ra.facility), fontWeight:700, fontSize:12, flexShrink:0 }}>
                {(ra.name||"?").split(" ").filter(Boolean).map(n=>n[0]).slice(0,2).join("").toUpperCase()}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:600, color:DS.textPrimary }}>{ra.name}</div>
                <div style={{ fontSize:11, color:facilityColor(ra.facility), marginTop:1 }}>Primary: {ra.facility} · PIN valid at both houses</div>
              </div>
              <div style={{ display:"flex", gap:6 }}>
                <button onClick={() => { setEditingId(editingId===ra.id?null:ra.id); setEditPin(""); }}
                  style={{ padding:"5px 12px", background:DS.blueTint, border:`1px solid ${DS.blueBorder}`, color:DS.blue, borderRadius:7, fontSize:12, cursor:"pointer" }}>
                  Reset PIN
                </button>
                <button onClick={() => handleDeactivate(ra)}
                  style={{ padding:"5px 12px", background:DS.error.bg, border:`1px solid ${DS.error.border}`, color:DS.error.text, borderRadius:7, fontSize:12, cursor:"pointer" }}>
                  Remove
                </button>
              </div>
            </div>
            {editingId===ra.id && (
              <div style={{ marginTop:10, paddingTop:10, borderTop:`1px solid ${DS.border}`, display:"flex", gap:8, alignItems:"center" }}>
                <input style={{ ...S.input, width:150 }} type="password" inputMode="numeric" maxLength={4}
                  placeholder="New 4-digit PIN" value={editPin}
                  onChange={e => setEditPin(e.target.value.replace(/\D/g,"").slice(0,4))} />
                <button onClick={() => handleResetPin(ra)} disabled={editPin.length!==4}
                  style={{ ...S.btn, padding:"7px 14px", fontSize:12, opacity:editPin.length===4?1:0.4 }}>Save PIN</button>
                <button onClick={() => { setEditingId(null); setEditPin(""); }}
                  style={{ ...S.btnGhost, padding:"7px 12px", fontSize:12 }}>Cancel</button>
              </div>
            )}
          </div>
        ))}
        {filtered.length===0 && (
          <div style={{ textAlign:"center", padding:"40px 0", color:DS.textSec }}>
            <div style={{ fontSize:28, marginBottom:8 }}>👤</div>
            <p style={{ fontSize:13, margin:0 }}>No RA accounts yet. Click "+ Add RA" to create one.</p>
          </div>
        )}
      </div>

      <div style={{ marginTop:14, padding:"10px 14px", background:DS.blueTint, border:`1px solid ${DS.blueBorder}`, borderRadius:8 }}>
        <p style={{ fontSize:12, color:DS.textSec, margin:0, lineHeight:1.6 }}>
          <strong style={{ color:DS.blue }}>Note:</strong> RA PINs are valid at both houses. Primary house is used for display only. RAs can also self-register their PIN from the house computer login screen.
        </p>
      </div>
    </div>
  );
}

// ── Main StaffManager ─────────────────────────────────────
export default function StaffManager() {
  const [tab, setTab] = useState("ra");
  const tabs = [
    { id:"ra",     label:"RA Accounts (PIN Login)" },
    { id:"google", label:"Google Accounts (Admin / PM)" },
  ];
  return (
    <div>
      <div style={{ display:"flex", gap:4, marginBottom:20, background:DS.pageBg, border:`1px solid ${DS.border}`, borderRadius:8, padding:3, width:"fit-content" }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding:"7px 18px", fontSize:12, fontWeight:tab===t.id?600:400, borderRadius:6, cursor:"pointer",
              background:tab===t.id ? DS.blue : "white", border:"none",
              color:tab===t.id ? "white" : DS.textPrimary, fontFamily:"inherit" }}>
            {t.label}
          </button>
        ))}
      </div>
      {tab==="ra"     && <RAAccountsTab/>}
      {tab==="google" && <GoogleAccountsTab/>}
    </div>
  );
}
